import type { OfflineSyncPayload, OfflineSyncUploadResult } from '../../domain/offlineSyncPayload.ts';
import type { HttpTransportPort } from '@core/domain/ports/driven/HttpTransportPort.ts';
import { withSyncFetchRetry } from './syncFetchRetry.ts';
import { extractSyncUploadMeta, extractSyncUploadWarnings, tryParseSyncUploadBodyJson } from './parseSyncUploadResponseBody.ts';

const SYNC_POST_TIMEOUT_MS = 120_000;

/**
 * POST JSON to the ingest URL (`SYNC_UPLOAD_*`).
 * When `syncBearerToken` is set (non-whitespace string), sends `Authorization: Bearer …` per server contract (same token as catalog download).
 * When `sendIdempotencyKeyHeader` is false, omits `Idempotency-Key` so browser CORS preflight succeeds if that header is absent from Allow-Headers; the payload still carries `idempotencyKey` in JSON.
 */
export async function postOfflineSyncPayload(
  url: string,
  payload: OfflineSyncPayload,
  transport: HttpTransportPort,
  onProgress?: (loaded: number, total: number) => void,
  syncBearerToken?: string | null,
  sendIdempotencyKeyHeader = true,
): Promise<OfflineSyncUploadResult> {
  return withSyncFetchRetry(async () => {
    const signal =
      typeof globalThis.AbortSignal !== 'undefined' && typeof globalThis.AbortSignal.timeout === 'function'
        ? globalThis.AbortSignal.timeout(SYNC_POST_TIMEOUT_MS)
        : undefined;

    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    if (sendIdempotencyKeyHeader) headers['Idempotency-Key'] = payload.idempotencyKey;
    const trimmedToken = typeof syncBearerToken === 'string' ? syncBearerToken.trim() : '';
    if (trimmedToken.length > 0) {
      headers.Authorization = `Bearer ${trimmedToken}`;
    }

    const response = await transport.request(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      onProgress,
      ...(signal ? { signal } : {}),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      const hint = body.trim() ? `: ${body.slice(0, 240)}` : '';
      throw new Error(`HTTP ${response.status}${hint}`);
    }

    const text = await response.text().catch(() => '');
    const parsed = tryParseSyncUploadBodyJson(text);
    const meta = extractSyncUploadMeta(parsed);
    return { warnings: extractSyncUploadWarnings(parsed), ...meta };
  });
}
